import { Injectable } from '@angular/core';
import { BehaviorSubject, combineLatest } from 'rxjs';
import { distinctUntilChanged, map } from 'rxjs/operators';

export type TranslationLoadData = {
  type: TranslationLoadType;
  extend?: boolean;
};

export enum TranslationLoadType {
  'FEATURE' = 'feature',
  'LIB' = 'lib',
}

enum LoadState {
  'START' = 1,
  'END' = -1,
}

@Injectable({
  providedIn: 'root',
})
export class TranslationStatusService {
  private readonly _loadingCoreFeatures$ = new BehaviorSubject<number>(0);
  readonly loadingCoreFeatures$ = this._loadingCoreFeatures$
    .asObservable()
    .pipe(
      map((v) => !!v),
      distinctUntilChanged(),
    );

  private readonly _loadingExtendedFeatures$ = new BehaviorSubject<number>(0);
  readonly loadingExtendedFeatures$ = this._loadingExtendedFeatures$
    .asObservable()
    .pipe(
      map((v) => !!v),
      distinctUntilChanged(),
    );

  private readonly _loadingLibs$ = new BehaviorSubject<number>(0);
  readonly loadingLibs$ = this._loadingLibs$.asObservable().pipe(
    map((v) => !!v),
    distinctUntilChanged(),
  );

  readonly loadingFeatures$ = combineLatest([
    this.loadingCoreFeatures$,
    this.loadingExtendedFeatures$,
  ]).pipe(
    map(([core, extended]) => core || extended),
    distinctUntilChanged(),
  );

  readonly loading$ = combineLatest([
    this.loadingCoreFeatures$,
    this.loadingExtendedFeatures$,
    this.loadingLibs$,
  ]).pipe(
    map(([core, extended, lib]) => core || extended || lib),
    distinctUntilChanged(),
  );

  onLoadStart(data: TranslationLoadData) {
    this.onLoad(data, LoadState.START);
  }

  onLoadEnd(data: TranslationLoadData) {
    this.onLoad(data, LoadState.END);
  }

  private onLoad({ type, extend }: TranslationLoadData, state: LoadState) {
    if (type === TranslationLoadType.FEATURE) {
      if (extend) {
        this._loadingExtendedFeatures$.next(
          this._loadingExtendedFeatures$.value + state,
        );
      } else {
        this._loadingCoreFeatures$.next(
          this._loadingCoreFeatures$.value + state,
        );
      }
    } else if (type === TranslationLoadType.LIB) {
      this._loadingLibs$.next(this._loadingLibs$.value + state);
    }
  }
}
